import React, { useRef, useState, useEffect } from "react";
import "../styles/PitchStyles.css";
import { useNavigate } from "react-router-dom";
import { sendToOpenAI } from "./OpenAiInteractions";
import axios from "axios";

const headerHeight = "h-16"; // 64px

type Message = {
  text: string;
  sender: "user" | "receiver";
  file?: File | null;
};

const PitchFabriceTextView: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [textValue, setTextValue] = useState("");
  const [messages, setMessages] = useState<Message[]>([
    {
      text: "Hey! there how can I help you",
      sender: "receiver",
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | undefined>(undefined);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handlePaperclipClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    if (file && file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleSend = async () => {
    if (!textValue.trim() && !selectedFile) return;

    // Add user message
    setMessages((prev) => [
      ...prev,
      { text: textValue, sender: "user", file: selectedFile },
    ]);
    const userText = textValue;
    setTextValue("");
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setTimeout(() => {
      textInputRef.current?.focus();
    }, 0);

    if (userText.trim()) {
      setIsLoading(true);
      // Add an empty receiver message to show loading dots
      setMessages((prev) => [...prev, { text: "", sender: "receiver" }]);

      try {
        const { response: fullResponse, threadId: newThreadId } =
          await sendToOpenAI(
            userText,
            (chunk: string) => {
              // Update the last receiver message with each chunk
              setMessages((prev) => {
                const updated = [...prev];
                const lastMessageIndex = updated.length - 1;
                if (
                  lastMessageIndex >= 0 &&
                  updated[lastMessageIndex].sender === "receiver"
                ) {
                  updated[lastMessageIndex] = {
                    ...updated[lastMessageIndex],
                    text: updated[lastMessageIndex].text + chunk,
                  };
                }
                return updated;
              });
            },
            threadId
          );

        // Store threadId if received
        if (newThreadId) {
          setThreadId(newThreadId);
        }

        // Ensure the final message is complete
        setMessages((prev) => {
          const updated = [...prev];
          const lastMessageIndex = updated.length - 1;
          if (
            lastMessageIndex >= 0 &&
            updated[lastMessageIndex].sender === "receiver"
          ) {
            updated[lastMessageIndex] = {
              ...updated[lastMessageIndex],
              text: fullResponse,
            };
          }
          return updated;
        });
      } catch (error) {
        console.error("Error in handleSend:", error);
        setMessages((prev) => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            text: "Sorry, there was an error contacting the API.",
            sender: "receiver",
          };
          return updated;
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Start recording audio
  const handleStartRecording = async () => {
    setRecordingError(null);
    setAudioChunks([]);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Use audio/webm for Chrome compatibility
      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = recorder;
      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        // Create blob and transcribe
        const audioBlob = new Blob(chunks, { type: "audio/webm" });
        if (audioBlob.size > 0) {
          await transcribeAudio(audioBlob);
        }
      };
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      setRecordingError("Microphone access denied or not available.");
    }
  };

  // Stop recording and trigger onstop
  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };

  // Transcribe audio and send as user message
  const transcribeAudio = async (audioBlob: Blob) => {
    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "audio.webm");
      formData.append("model", "whisper-1");
      formData.append("language", "en");
      const response = await axios.post(
        "https://api.openai.com/v1/audio/transcriptions",
        formData,
        {
          headers: {
            Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const text = response.data.text?.trim();
      if (text) {
        // Add user message and trigger OpenAI response
        setMessages((prev) => [...prev, { text, sender: "user" }]);
        setTextValue("");
        setIsLoading(true);
        setMessages((prev) => [...prev, { text: "", sender: "receiver" }]);
        try {
          const { response: fullResponse, threadId: newThreadId } =
            await sendToOpenAI(
              text,
              (chunk: string) => {
                setMessages((prev) => {
                  const updated = [...prev];
                  const lastMessageIndex = updated.length - 1;
                  if (
                    lastMessageIndex >= 0 &&
                    updated[lastMessageIndex].sender === "receiver"
                  ) {
                    updated[lastMessageIndex] = {
                      ...updated[lastMessageIndex],
                      text: updated[lastMessageIndex].text + chunk,
                    };
                  }
                  return updated;
                });
              },
              threadId
            );
          if (newThreadId) setThreadId(newThreadId);
          setMessages((prev) => {
            const updated = [...prev];
            const lastMessageIndex = updated.length - 1;
            if (
              lastMessageIndex >= 0 &&
              updated[lastMessageIndex].sender === "receiver"
            ) {
              updated[lastMessageIndex] = {
                ...updated[lastMessageIndex],
                text: fullResponse,
              };
            }
            return updated;
          });
        } catch (error) {
          setMessages((prev) => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              text: "Sorry, there was an error contacting the API.",
              sender: "receiver",
            };
            return updated;
          });
        } finally {
          setIsLoading(false);
        }
      }
    } catch (error) {
      setRecordingError("Failed to transcribe audio.");
    } finally {
      setIsLoading(false); // Hide spinner and stop button after API is fetched
      setIsRecording(false); // Now hide stop button and show mic
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  return (
    <div className="relative h-screen flex flex-col font-inter">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 text-black flex items-center justify-between ${headerHeight} bg-white`}
      >
        <div className="menu-section flex items-center pl-3">
          <img
            src="/fabric-favicon.png"
            alt="FG Logo"
            className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 object-contain"
            style={{ maxWidth: "2.5rem", maxHeight: "2.5rem" }}
          />
        </div>
        <div className="profile-name-section flex items-center gap-3">
          <div className="relative">
            <img
              src="/fabrice-profile.jpg"
              alt="Fabrice Avatar"
              className="w-9 h-9 rounded-full object-cover border border-gray-200"
              style={{ background: "#f1f0ef" }}
            />
            <span
              className="absolute"
              style={{
                bottom: 2,
                right: 2,
                width: "12px",
                height: "12px",
                background: "#30a46c",
                borderRadius: "50%",
                border: "2px solid #fff",
                display: "block",
              }}
            />
          </div>
          <span>Pitch Fabrice</span>
        </div>
        <div className="profile-icons-section me-3 flex items-center gap-3">
          <button
            type="button"
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100"
            style={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
            aria-label="Call"
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => navigate("/pitch-voice")}
          >
            <img
              src="/phone.png"
              alt="Phone"
              style={{ width: 20, height: 20, display: "block" }}
            />
          </button>
        </div>
      </header>

      {/* Body */}
      <main className={`flex-1 overflow-y-auto pt-16 pb-28 custom-scrollbar`}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex flex-col gap-4 py-4">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className="flex flex-col items-end gap-1 max-w-[70%]"
                  style={{
                    alignItems:
                      msg.sender === "user" ? "flex-end" : "flex-start",
                  }}
                >
                  {msg.file && (
                    <div className="relative w-fit max-w-xs bg-white rounded-xl shadow-md border border-gray-200 flex items-center p-2 pr-10 mb-1">
                      <img
                        src="/doc.png"
                        alt="document"
                        className="w-8 h-8 object-cover rounded-md mr-3"
                      />
                      <span className="text-sm text-gray-800 truncate max-w-[160px] filename">
                        {msg.file.name}
                      </span>
                    </div>
                  )}
                  {msg.text && msg.text.trim() && (
                    <div
                      className={`rounded-xl shadow-md px-4 py-3`}
                      style={{
                        alignSelf:
                          msg.sender === "user" ? "flex-end" : "flex-start",
                        wordBreak: "break-word",
                        backgroundColor:
                          msg.sender === "user" ? "#f76b15" : "#e9e8e6",
                        color: msg.sender === "user" ? "#fff" : "#222",
                        fontFamily: '"Poppins", sans-serif',
                      }}
                    >
                      {msg.text}
                    </div>
                  )}
                  {msg.sender === "receiver" &&
                    isLoading &&
                    idx === messages.length - 1 &&
                    (!msg.text || !msg.text.trim()) && (
                      <div
                        className="rounded-xl shadow-md px-4 py-3 flex items-center"
                        style={{
                          alignSelf: "flex-start",
                          backgroundColor: "#e9e8e6",
                          color: "#222",
                          fontFamily: '"Poppins", sans-serif',
                          minHeight: "2.25rem",
                          minWidth: "2.5rem",
                        }}
                      >
                        <span className="jumping-dots">
                          <span className="dot">.</span>
                          <span className="dot">.</span>
                          <span className="dot">.</span>
                        </span>
                      </div>
                    )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </main>

      {/* Input Field Fixed at Bottom */}
      <div className="fixed bottom-4 left-0 right-0 flex justify-center z-50">
        <div className="w-full max-w-3xl px-4">
          <div
            className="relative"
            style={{ minHeight: selectedFile ? "7rem" : undefined }}
          >
            {selectedFile && (
              <div
                className="absolute left-0 top-0 flex items-center w-full px-8 pt-2 pb-3 pointer-events-none"
                style={{
                  alignItems: "flex-start",
                  height: "4.5rem",
                }}
              >
                <div className="relative w-fit max-w-xs bg-white rounded-xl shadow-md border border-gray-200 flex items-center p-2 pr-10">
                  <img
                    src="/doc.png"
                    alt="document"
                    className="w-8 h-8 object-cover rounded-md mr-3"
                  />
                  <span className="text-sm text-gray-800 truncate max-w-[160px] filename">
                    {selectedFile.name}
                  </span>
                  <button
                    type="button"
                    className="absolute top-1 right-1 text-gray-400 hover:text-red-500 text-lg"
                    aria-label="Remove file"
                    style={{ pointerEvents: "auto" }}
                    onClick={() => {
                      setSelectedFile(null);
                      setPreviewUrl(null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                      setTimeout(() => {
                        textInputRef.current?.focus();
                      }, 0);
                    }}
                  >
                    ×
                  </button>
                </div>
              </div>
            )}
            {/* Move paperclip button here, after file preview, before input */}
            <button
              type="button"
              onClick={handlePaperclipClick}
              className="absolute flex items-center focus:outline-none ml-2"
              tabIndex={0}
              aria-label="Attach file"
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                left: 0,
                top: selectedFile ? "4.2rem" : "0.3rem", // Slightly move up for better centering
                transform: "none",
                height: "2.5rem",
              }}
            >
              <svg
                width="20"
                height="20"
                fill="none"
                viewBox="0 0 20 20"
                style={{ color: "#b5b5b1", transform: "rotate(-45deg)" }}
              >
                <path
                  d="M6.5 10.5l5.5-5.5a3 3 0 114.24 4.24l-7.07 7.07a4 4 0 01-5.66-5.66l7.07-7.07"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              tabIndex={-1}
              onChange={handleFileChange}
            />
            <input
              type="text"
              className={`w-full border border-gray-300 px-8 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm ${
                selectedFile ? "rounded-lg" : "rounded-full"
              }`}
              placeholder="Type your message..."
              style={{
                paddingLeft: "2.5rem",
                paddingRight: "2.5rem",
                paddingTop: selectedFile ? "4.5rem" : undefined,
              }}
              ref={textInputRef}
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onKeyDown={(e) => {
                if (
                  (e.key === "Enter" || e.keyCode === 13) &&
                  (textValue.trim() || selectedFile)
                ) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <span
              className="absolute right-0 flex items-center pr-3"
              style={{
                top: selectedFile ? "4.2rem" : "0.3rem",
                height: "2.5rem",
                pointerEvents: "auto",
              }}
            >
              {/* Mic/Stop/Send Button */}
              {selectedFile || textValue.trim() ? (
                <img
                  src="/up-arrow.png"
                  alt="Send"
                  style={{
                    width: 20,
                    height: 20,
                    display: "block",
                    cursor: "pointer",
                  }}
                  onClick={handleSend}
                  tabIndex={0}
                  role="button"
                  aria-label="Send message"
                />
              ) : isRecording ? (
                <div style={{ position: "relative", display: "inline-block" }}>
                  <button
                    type="button"
                    onClick={() => {
                      // Show spinner immediately, but keep stop button visible until API finishes
                      setIsLoading(true);
                      handleStopRecording();
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: isLoading ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                      minWidth: "32px",
                      minHeight: "32px",
                      opacity: isLoading ? 0.7 : 1,
                    }}
                    aria-label="Stop recording"
                    disabled={isLoading}
                  >
                    <img
                      src="/stop.png"
                      alt="Stop"
                      style={{
                        width: 20,
                        height: 20,
                        display: "block",
                        opacity: isLoading ? 0.5 : 1,
                      }}
                    />
                    {isLoading && <span className="spinner-red-border" />}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleStartRecording}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                  aria-label="Start recording"
                >
                  <img
                    src="/mic.png"
                    alt="Mic"
                    style={{ width: 20, height: 20, display: "block" }}
                  />
                </button>
              )}
            </span>
            {recordingError && (
              <div className="text-red-600 text-xs mt-2">{recordingError}</div>
            )}
          </div>
        </div>
      </div>
      <style>
        {`
          .jumping-dots {
            display: flex;
            gap: 2px;
            font-size: 1.5rem;
            font-weight: bold;
            letter-spacing: 1px;
          }
          .jumping-dots .dot {
            animation: jump 1s infinite;
            display: inline-block;
          }
          .jumping-dots .dot:nth-child(2) {
            animation-delay: 0.2s;
          }
          .jumping-dots .dot:nth-child(3) {
            animation-delay: 0.4s;
          }
          @keyframes jump {
            0%, 80%, 100% { transform: translateY(0); }
            40% { transform: translateY(-8px); }
          }
          .spinner-red-border {
            border: 3px solid #ff4d4f;
            border-top: 3px solid transparent;
            border-radius: 50%;
            width: 36px;
            height: 36px;
            position: absolute;
            top: 50%;
            left: 50%;
            margin-top: -18px;
            margin-left: -18px;
            animation: spin 1s linear infinite;
            z-index: 2;
            pointer-events: none;
          }
          @keyframes spin {
            0% { transform: translate(-50%, -50%) rotate(0deg);}
            100% { transform: translate(-50%, -50%) rotate(360deg);}
          }
        `}
      </style>
    </div>
  );
};

export default PitchFabriceTextView;
